/**
 * Sound and desktop-notification settings of the bell, per browser.
 *
 * Stored in localStorage on purpose: a salon PC at the front desk wants the
 * chime loud, the owner's laptop at home maybe muted — that is a property of
 * the device, not of the account. Every storage access is wrapped: a private
 * window or blocked site data must never break the bell, it just falls back to
 * the defaults.
 */

export interface NotificationPrefs {
  /** Master switch for every chime. */
  soundEnabled: boolean;
  /** Quiet chime for "Kunde schreibt mit dem Bot" and running calls. */
  softSoundsEnabled: boolean;
  /** 0 … 1 */
  volume: number;
  /** Desktop notification while the tab is in the background (needs browser permission). */
  desktopEnabled: boolean;
}

export const NOTIFICATION_PREFS_KEY = 'tg.notifications.prefs.v1';

export const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = Object.freeze({
  soundEnabled: true,
  softSoundsEnabled: true,
  volume: 0.7,
  desktopEnabled: false,
});

function clampVolume(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return DEFAULT_NOTIFICATION_PREFS.volume;
  return Math.min(1, Math.max(0, value));
}

function bool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/** Validate field by field: one broken value must not reset the others. */
export function parseNotificationPrefs(raw: string | null): NotificationPrefs {
  if (!raw) return { ...DEFAULT_NOTIFICATION_PREFS };
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return { ...DEFAULT_NOTIFICATION_PREFS };
  }
  if (!data || typeof data !== 'object') return { ...DEFAULT_NOTIFICATION_PREFS };
  const stored = data as Record<string, unknown>;
  return {
    soundEnabled: bool(stored.soundEnabled, DEFAULT_NOTIFICATION_PREFS.soundEnabled),
    softSoundsEnabled: bool(stored.softSoundsEnabled, DEFAULT_NOTIFICATION_PREFS.softSoundsEnabled),
    volume: clampVolume(stored.volume),
    desktopEnabled: bool(stored.desktopEnabled, DEFAULT_NOTIFICATION_PREFS.desktopEnabled),
  };
}

export function loadNotificationPrefs(): NotificationPrefs {
  try {
    return parseNotificationPrefs(window.localStorage.getItem(NOTIFICATION_PREFS_KEY));
  } catch {
    return { ...DEFAULT_NOTIFICATION_PREFS };
  }
}

export function saveNotificationPrefs(prefs: NotificationPrefs): void {
  try {
    window.localStorage.setItem(NOTIFICATION_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // Private window / quota: the setting still applies for this page view.
  }
}

/** Follow changes made in another tab (the `storage` event only fires in the OTHER tabs). */
export function subscribeNotificationPrefs(listener: (prefs: NotificationPrefs) => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key !== NOTIFICATION_PREFS_KEY) return;
    listener(parseNotificationPrefs(event.newValue));
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}
